import { useMemo } from 'react';
import {
  AreaChart,
  Area,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import { formatCurrency } from '@shared/SharedAccountBody';
import type {
  PortfolioEquityPoint,
  StockBar,
  StockChartRange,
} from '@shared/types';
import { COLORS } from '../../constants/theme';

const RANGE_OPTIONS: StockChartRange[] = ['1D', '1W', '1M', '1Y', 'ALL'];
const DOWN_COLOR = '#ff4d5e';
const PORTFOLIO_COLOR = COLORS.primaryBlue;

export type ChartMode = '$' | '%';

interface PortfolioStockChartProps {
  symbol: string;
  bars?: StockBar[];
  loading?: boolean;
  error?: string | null;
  range: StockChartRange;
  onRangeChange: (range: StockChartRange) => void;
  mode: ChartMode;
  onModeChange: (mode: ChartMode) => void;
  portfolioPoints?: PortfolioEquityPoint[];
  /** Average entry price — drawn as a horizontal ENTRY reference line. */
  avgEntryPrice?: number;
  /** Earliest buy fill for this position (ISO) — drawn as a BUY marker. */
  heldSince?: string;
  /** Most recent sell fill for this symbol (ISO) — drawn as a SOLD marker. */
  lastSoldAt?: string;
}

interface ChartRow {
  label: string;
  timeMs: number;
  stockPrice: number;
  stock: number;
  portfolioPct: number | null;
}

function formatBarLabel(iso: string, range: StockChartRange): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const tz = 'America/New_York';
  if (range === '1D' || range === '1W') {
    return date.toLocaleTimeString('en-US', {
      timeZone: tz,
      hour: 'numeric',
      minute: '2-digit',
    });
  }
  return date.toLocaleDateString('en-US', {
    timeZone: tz,
    month: 'short',
    day: 'numeric',
    ...(range === '1Y' || range === 'ALL' ? { year: '2-digit' } : {}),
  });
}

const formatPct = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;

function paddedDomain(values: number[]): [number, number] {
  if (values.length === 0) return [0, 1];
  const min = Math.min(...values);
  const max = Math.max(...values);
  const pad = (max - min) * 0.08 || Math.abs(max) * 0.02 || 1;
  return [min - pad, max + pad];
}

const PortfolioStockChart = ({
  symbol,
  bars,
  loading,
  error,
  range,
  onRangeChange,
  mode,
  onModeChange,
  portfolioPoints,
  avgEntryPrice,
  heldSince,
  lastSoldAt,
}: PortfolioStockChartProps) => {
  const rows = useMemo<ChartRow[]>(() => {
    const sortedBars = (bars ?? [])
      .map((b) => ({ timeMs: Date.parse(b.t), price: b.c, iso: b.t }))
      .filter((b) => !Number.isNaN(b.timeMs))
      .sort((a, b) => a.timeMs - b.timeMs);
    if (sortedBars.length === 0) return [];

    const stockBaseline = sortedBars[0]!.price;

    const port = (portfolioPoints ?? [])
      .map((p) => ({ timeMs: Date.parse(p.t), equity: p.equity }))
      .filter((p) => !Number.isNaN(p.timeMs) && p.equity > 0)
      .sort((a, b) => a.timeMs - b.timeMs);

    // Portfolio baseline = equity at or before the first stock bar (else the
    // first available point), so both series are rebased to the window start.
    const firstBarTime = sortedBars[0]!.timeMs;
    let baseIdx = -1;
    for (let i = 0; i < port.length; i++) {
      if (port[i]!.timeMs <= firstBarTime) baseIdx = i;
      else break;
    }
    const portBaseline =
      baseIdx >= 0 ? port[baseIdx]!.equity : port[0]?.equity;

    let pIdx = 0;
    return sortedBars.map((bar) => {
      while (pIdx + 1 < port.length && port[pIdx + 1]!.timeMs <= bar.timeMs) {
        pIdx += 1;
      }
      let portfolioPct: number | null = null;
      const aligned = port[pIdx];
      if (
        portBaseline &&
        portBaseline > 0 &&
        aligned &&
        aligned.timeMs <= bar.timeMs
      ) {
        portfolioPct =
          Math.round((aligned.equity / portBaseline - 1) * 100 * 100) / 100;
      }

      const stockPct =
        stockBaseline > 0
          ? Math.round((bar.price / stockBaseline - 1) * 100 * 100) / 100
          : 0;

      return {
        label: formatBarLabel(bar.iso, range),
        timeMs: bar.timeMs,
        stockPrice: bar.price,
        stock: mode === '$' ? bar.price : stockPct,
        portfolioPct,
      };
    });
  }, [bars, portfolioPoints, mode, range]);

  const hasPortfolio = useMemo(
    () => rows.some((r) => r.portfolioPct !== null),
    [rows],
  );

  const firstPrice = rows[0]?.stockPrice;
  const lastPrice = rows[rows.length - 1]?.stockPrice;
  const isUp =
    firstPrice !== undefined && lastPrice !== undefined
      ? lastPrice >= firstPrice
      : true;
  const stockColor = isUp ? COLORS.portfolioGreen : DOWN_COLOR;

  const changeAmount =
    firstPrice !== undefined && lastPrice !== undefined
      ? lastPrice - firstPrice
      : undefined;
  const changePct =
    firstPrice !== undefined && lastPrice !== undefined && firstPrice !== 0
      ? (changeAmount! / firstPrice) * 100
      : undefined;

  // Average-entry level on the stock (left) axis, so the gap to the current
  // price reads as P/L since purchase.
  const entryValue = useMemo(() => {
    if (!avgEntryPrice || avgEntryPrice <= 0 || rows.length === 0) return undefined;
    const baseline = rows[0]!.stockPrice;
    if (mode === '$') return avgEntryPrice;
    return baseline > 0
      ? Math.round((avgEntryPrice / baseline - 1) * 100 * 100) / 100
      : undefined;
  }, [avgEntryPrice, rows, mode]);

  const { leftDomain, rightDomain } = useMemo(() => {
    const stockVals = rows.map((r) => r.stock);
    if (entryValue !== undefined) stockVals.push(entryValue);
    const portVals = rows
      .map((r) => r.portfolioPct)
      .filter((v): v is number => v !== null);
    if (mode === '%') {
      // Shared % scale so the two lines compare 1:1 across both axes.
      const shared = paddedDomain([...stockVals, ...portVals]);
      return { leftDomain: shared, rightDomain: shared };
    }
    return {
      leftDomain: paddedDomain(stockVals),
      rightDomain: paddedDomain(portVals),
    };
  }, [rows, mode, entryValue]);

  // Place BUY / SOLD markers on the nearest bar within the visible window.
  const markers = useMemo(() => {
    if (rows.length === 0) return [] as { label: string; kind: 'BUY' | 'SOLD' }[];
    const first = rows[0]!.timeMs;
    const last = rows[rows.length - 1]!.timeMs;
    const nearest = (iso?: string): string | null => {
      if (!iso) return null;
      const t = Date.parse(iso);
      if (Number.isNaN(t) || t < first || t > last) return null;
      let best = rows[0]!;
      let bestDelta = Math.abs(rows[0]!.timeMs - t);
      for (const r of rows) {
        const d = Math.abs(r.timeMs - t);
        if (d < bestDelta) {
          best = r;
          bestDelta = d;
        }
      }
      return best.label;
    };
    const out: { label: string; kind: 'BUY' | 'SOLD' }[] = [];
    const buy = nearest(heldSince);
    if (buy) out.push({ label: buy, kind: 'BUY' });
    const sold = nearest(lastSoldAt);
    if (sold) out.push({ label: sold, kind: 'SOLD' });
    return out;
  }, [rows, heldSince, lastSoldAt]);

  const StockTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload?.length) return null;
    const row: ChartRow | undefined = payload[0]?.payload;
    return (
      <div className="pointer-events-none rounded-lg border border-border bg-[color:var(--nq-bg-deep)]/98 px-3 py-2 text-[13px] shadow-[var(--shadow-popover)]">
        <p className="mb-1.5 text-xs text-muted-foreground">{label}</p>
        <div className="grid grid-cols-[minmax(0,1fr)_auto_64px] items-center gap-3 whitespace-nowrap py-0.5 font-semibold">
          <span style={{ color: stockColor }}>{symbol}</span>
          <span className="text-right tabular-nums text-foreground">
            {row ? formatCurrency(row.stockPrice) : '—'}
          </span>
          {mode === '%' && row && (
            <span className="text-right tabular-nums">{formatPct(row.stock)}</span>
          )}
        </div>
        {hasPortfolio && row?.portfolioPct !== null && row?.portfolioPct !== undefined && (
          <div className="grid grid-cols-[minmax(0,1fr)_auto_64px] items-center gap-3 whitespace-nowrap py-0.5">
            <span style={{ color: PORTFOLIO_COLOR }}>NovaQuantis</span>
            <span />
            <span className="text-right tabular-nums">{formatPct(row.portfolioPct)}</span>
          </div>
        )}
      </div>
    );
  };

  const chartLoading = loading && rows.length === 0;

  return (
    <div>
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-baseline gap-2.5">
          {lastPrice !== undefined ? (
            <>
              <span className="font-display text-[1.4rem] font-semibold text-foreground">
                {formatCurrency(lastPrice)}
              </span>
              {changeAmount !== undefined && changePct !== undefined && (
                <span
                  className={`text-[13px] font-semibold ${
                    isUp ? 'text-positive' : 'text-negative'
                  }`}
                >
                  {changeAmount >= 0 ? '+' : ''}
                  {formatCurrency(changeAmount)} ({changePct >= 0 ? '+' : ''}
                  {changePct.toFixed(2)}%)
                </span>
              )}
            </>
          ) : (
            <span className="font-display text-[1.4rem] font-semibold text-foreground">—</span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2.5">
          <div
            className="inline-flex overflow-hidden rounded-lg border border-border"
            role="group"
            aria-label="Value mode"
          >
            {(['%', '$'] as ChartMode[]).map((m) => (
              <button
                key={m}
                type="button"
                className={`cursor-pointer appearance-none border-0 px-2.5 py-[5px] text-xs font-bold transition-[background,color] ${
                  mode === m
                    ? 'bg-primary text-[color:var(--nq-bg-deep)]'
                    : 'bg-transparent text-muted-foreground'
                }`}
                onClick={() => onModeChange(m)}
                aria-pressed={mode === m}
              >
                {m}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {RANGE_OPTIONS.map((opt) => (
              <button
                key={opt}
                type="button"
                className={`cursor-pointer appearance-none rounded-md border border-solid px-2.5 py-1 text-xs transition-colors ${
                  range === opt
                    ? 'border-primary/40 bg-primary/10 text-primary'
                    : 'border-border bg-transparent text-muted-foreground hover:border-[color:rgba(255,255,255,0.15)] hover:text-foreground/65'
                }`}
                onClick={() => onRangeChange(opt)}
                aria-pressed={range === opt}
              >
                {opt}
              </button>
            ))}
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-2 rounded-lg border border-[color:rgba(220,80,80,0.25)] bg-[color:rgba(220,80,80,0.12)] px-3 py-2 text-[13px] text-[color:var(--nq-down-soft)]">
          {error}
        </div>
      )}

      {chartLoading ? (
        <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
          Loading chart…
        </div>
      ) : rows.length === 0 ? (
        <div className="flex min-h-[280px] items-center justify-center text-sm text-muted-foreground">
          {error
            ? 'Price history is unavailable for this range.'
            : `No price data for ${symbol} in this range.`}
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={rows} margin={{ top: 4, right: 12, left: 8, bottom: 0 }}>
            <defs>
              <linearGradient id="stockPriceFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={stockColor} stopOpacity={0.4} />
                <stop offset="100%" stopColor={stockColor} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" strokeDasharray="4 4" />
            <XAxis
              dataKey="label"
              tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={40}
            />
            <YAxis
              yAxisId="left"
              domain={leftDomain}
              tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={(v) =>
                mode === '$' ? `$${Number(v).toFixed(0)}` : `${Number(v).toFixed(0)}%`
              }
              width={56}
            />
            {hasPortfolio && (
              <YAxis
                yAxisId="right"
                orientation="right"
                domain={rightDomain}
                tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 11 }}
                axisLine={false}
                tickLine={false}
                tickFormatter={(v) => `${Number(v).toFixed(0)}%`}
                width={48}
              />
            )}
            <Tooltip
              content={<StockTooltip />}
              wrapperStyle={{ zIndex: 20, outline: 'none' }}
              contentStyle={{
                background: 'transparent',
                border: 'none',
                boxShadow: 'none',
                padding: 0,
                margin: 0,
              }}
              itemStyle={{ display: 'none' }}
              labelStyle={{ display: 'none' }}
            />
            <Legend
              wrapperStyle={{ paddingBottom: 8, fontSize: 12, color: 'rgba(255,255,255,0.7)' }}
            />
            {entryValue !== undefined && avgEntryPrice !== undefined && (
              <ReferenceLine
                yAxisId="left"
                y={entryValue}
                stroke={COLORS.primaryOrange}
                strokeDasharray="4 4"
                label={{
                  value: `ENTRY ${formatCurrency(avgEntryPrice)}`,
                  position: 'insideTopLeft',
                  fill: COLORS.primaryOrange,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            )}
            {markers.map((m) => (
              <ReferenceLine
                key={`${m.kind}-${m.label}`}
                yAxisId="left"
                x={m.label}
                stroke={m.kind === 'BUY' ? COLORS.portfolioGreen : DOWN_COLOR}
                strokeDasharray="3 3"
                label={{
                  value: m.kind,
                  position: 'top',
                  fill: m.kind === 'BUY' ? COLORS.portfolioGreen : DOWN_COLOR,
                  fontSize: 10,
                  fontWeight: 700,
                }}
              />
            ))}
            <Area
              yAxisId="left"
              type="monotone"
              dataKey="stock"
              name={symbol}
              stroke={stockColor}
              strokeWidth={2}
              fill="url(#stockPriceFill)"
              isAnimationActive={false}
            />
            {hasPortfolio && (
              <Line
                yAxisId="right"
                type="monotone"
                dataKey="portfolioPct"
                name="NovaQuantis Portfolio"
                stroke={PORTFOLIO_COLOR}
                strokeWidth={1.5}
                strokeDasharray="6 4"
                dot={false}
                connectNulls
                isAnimationActive={false}
              />
            )}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default PortfolioStockChart;
